/* PayrollRuns.jsx — Monthly payroll orchestrator
 *
 * Pulls hours + commission + manual adjustments → previews → generates
 * payslips → optionally locks the period. All staff in one screen.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Wallet, Calculator, History, Lock, Trash2, Loader2, RefreshCw, Settings, Eye, Calendar, Printer, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => "R " + Math.round(Number(n || 0)).toLocaleString("en-ZA");
const fmtH = (n) => (Number(n || 0)).toFixed(2);

const MONTHS = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function PayrollRuns() {
  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="payroll-runs-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wallet className="h-6 w-6 text-emerald-600" />
            Monthly Payroll Runs
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Pulls clocked hours, commission, leave — produces payslips for all active staff in one go.
          </p>
        </div>
      </div>

      <Tabs defaultValue="new">
        <TabsList>
          <TabsTrigger value="new" data-testid="tab-new-run"><Calculator className="h-4 w-4 mr-1" /> New / Preview</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history"><History className="h-4 w-4 mr-1" /> Past Runs</TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config"><Settings className="h-4 w-4 mr-1" /> Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="new"><NewRunPanel /></TabsContent>
        <TabsContent value="history"><PastRunsPanel /></TabsContent>
        <TabsContent value="config"><ConfigPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ============ New / Preview Panel ============ */
function NewRunPanel() {
  const todaySAST = () => {
    const d = new Date();
    return new Date(d.getTime() + (2 * 60 - d.getTimezoneOffset()) * 60000);
  };
  const def = todaySAST();
  const [year, setYear] = useState(def.getUTCFullYear());
  const [month, setMonth] = useState(def.getUTCMonth() + 1);
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [preview, setPreview] = useState(null);
  const [adjustments, setAdjustments] = useState({});   // {employee_id: {bonus, other_earnings, ...}}
  const [busy, setBusy] = useState(false);
  const [runBusy, setRunBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = { year, month };
      if (periodStart) params.period_start = periodStart;
      if (periodEnd) params.period_end = periodEnd;
      const res = await axios.get(`${API}/api/payroll/preview`, { params });
      setPreview(res.data);
      // Reset adjustments when period changes
      setAdjustments({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month, periodStart, periodEnd]);

  const setAdj = (empId, field, value) => {
    setAdjustments((cur) => ({
      ...cur,
      [empId]: { ...(cur[empId] || {}), [field]: value },
    }));
  };

  // Recompute totals client-side when adjustments change
  const computedTotals = useMemo(() => {
    if (!preview) return null;
    let gross = 0, ded = 0, net = 0;
    preview.lines.forEach((ln) => {
      const a = adjustments[ln.employee_id] || {};
      const bonus = Number(a.bonus) || ln.bonus;
      const otherE = Number(a.other_earnings) || ln.other_earnings;
      const loan = Number(a.loan_deduction) || ln.loan_deduction;
      const otherD = Number(a.other_deductions) || ln.other_deductions;
      const g = ln.basic_pay + ln.overtime_pay + ln.commission + bonus + otherE;
      const d = ln.uif_deduction + ln.paye_deduction + loan + otherD;
      gross += g; ded += d; net += g - d;
    });
    return { gross: Math.round(gross), ded: Math.round(ded), net: Math.round(net) };
  }, [preview, adjustments]);

  const runPayroll = async () => {
    if (!preview) return;
    if (!window.confirm(`Run payroll for ${MONTHS[month]} ${year}? ${preview.lines.length} payslips will be generated as a DRAFT (you can review & lock after).`)) return;
    setRunBusy(true);
    try {
      const adjList = Object.entries(adjustments).map(([emp_id, vals]) => ({
        employee_id: emp_id,
        bonus: Number(vals.bonus) || 0,
        other_earnings: Number(vals.other_earnings) || 0,
        other_earnings_description: vals.other_earnings_description || "",
        loan_deduction: Number(vals.loan_deduction) || 0,
        other_deductions: Number(vals.other_deductions) || 0,
        other_deductions_description: vals.other_deductions_description || "",
      }));
      const res = await axios.post(`${API}/api/payroll/run`, {
        year, month,
        period_start: periodStart || null,
        period_end: periodEnd || null,
        adjustments: adjList,
      });
      toast.success(`Generated ${res.data.payslips.length} payslips · Net R ${res.data.run.total_net_pay.toLocaleString("en-ZA")}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payroll run failed");
    } finally {
      setRunBusy(false);
    }
  };

  const locked = preview?.existing_run?.status === "locked";

  return (
    <div className="space-y-4">
      {/* Period picker */}
      <Card>
        <CardContent className="pt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <Label className="text-xs">Year</Label>
            <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="year-input" />
          </div>
          <div>
            <Label className="text-xs">Month</Label>
            <select className="w-full h-10 border rounded px-2" value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="month-select">
              {MONTHS.slice(1).map((m, i) => (<option key={i + 1} value={i + 1}>{m}</option>))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Period start <span className="text-gray-400">(optional)</span></Label>
            <Input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Period end <span className="text-gray-400">(optional)</span></Label>
            <Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
          </div>
          <div className="flex items-end">
            <Button variant="outline" onClick={load} disabled={busy} data-testid="refresh-preview-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {locked && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded text-amber-800 text-sm">
          <Lock className="h-4 w-4" /> This month is LOCKED. To make changes, delete the locked run from Past Runs first (not allowed on locked).
        </div>
      )}

      {/* Preview table */}
      {preview && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center justify-between">
              <span>{MONTHS[month]} {year} · {preview.lines.length} employees</span>
              <Badge variant="outline" className="text-xs">
                Period {preview.period_start} → {preview.period_end}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table className="text-sm">
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Hrs / OT</TableHead>
                  <TableHead className="text-right">Basic</TableHead>
                  <TableHead className="text-right">OT pay</TableHead>
                  <TableHead className="text-right">Comm</TableHead>
                  <TableHead className="text-right">Bonus</TableHead>
                  <TableHead className="text-right">Other +</TableHead>
                  <TableHead className="text-right">PAYE</TableHead>
                  <TableHead className="text-right">UIF</TableHead>
                  <TableHead className="text-right">Other −</TableHead>
                  <TableHead className="text-right font-bold">Net</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.lines.map((ln) => {
                  const a = adjustments[ln.employee_id] || {};
                  const bonus = Number(a.bonus) || ln.bonus;
                  const otherE = Number(a.other_earnings) || ln.other_earnings;
                  const loan = Number(a.loan_deduction) || ln.loan_deduction;
                  const otherD = Number(a.other_deductions) || ln.other_deductions;
                  const g = ln.basic_pay + ln.overtime_pay + ln.commission + bonus + otherE;
                  const d = ln.uif_deduction + ln.paye_deduction + loan + otherD;
                  const net = Math.round(g - d);
                  return (
                    <TableRow key={ln.employee_id} className="[&_input]:text-xs [&_input]:h-7 [&_input]:px-1">
                      <TableCell className="font-medium">
                        {ln.employee_name}
                        <div className="text-[10px] text-gray-500">{ln.position}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-[10px] ${ln.salary_type === "monthly" ? "border-blue-400 text-blue-700" : "border-orange-400 text-orange-700"}`}>
                          {ln.salary_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-[11px]">
                        {fmtH(ln.regular_hours)}
                        {ln.overtime_hours > 0 && <span className="text-orange-600"> + {fmtH(ln.overtime_hours)}</span>}
                      </TableCell>
                      <TableCell className="text-right">{fmt(ln.basic_pay)}</TableCell>
                      <TableCell className="text-right">{ln.overtime_pay > 0 ? fmt(ln.overtime_pay) : "—"}</TableCell>
                      <TableCell className="text-right">{ln.commission > 0 ? fmt(ln.commission) : "—"}</TableCell>
                      <TableCell className="text-right">
                        <Input type="number" step="1" value={a.bonus ?? ""} placeholder="0" disabled={locked}
                          onChange={(e) => setAdj(ln.employee_id, "bonus", e.target.value)}
                          className="w-20 ml-auto text-right" />
                      </TableCell>
                      <TableCell className="text-right">
                        <Input type="number" step="1" value={a.other_earnings ?? ""} placeholder="0" disabled={locked}
                          onChange={(e) => setAdj(ln.employee_id, "other_earnings", e.target.value)}
                          className="w-20 ml-auto text-right" />
                      </TableCell>
                      <TableCell className="text-right text-red-700">{fmt(ln.paye_deduction)}</TableCell>
                      <TableCell className="text-right text-red-700">{fmt(ln.uif_deduction)}</TableCell>
                      <TableCell className="text-right">
                        <Input type="number" step="1" value={a.other_deductions ?? ""} placeholder="0" disabled={locked}
                          onChange={(e) => setAdj(ln.employee_id, "other_deductions", e.target.value)}
                          className="w-20 ml-auto text-right" />
                      </TableCell>
                      <TableCell className="text-right font-bold text-emerald-700">{fmt(net)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {/* Totals strip */}
            {computedTotals && (
              <div className="grid grid-cols-4 gap-3 mt-4 pt-3 border-t">
                <Stat label="Total Gross" value={fmt(computedTotals.gross)} />
                <Stat label="Total Deductions" value={fmt(computedTotals.ded)} cls="text-red-700" />
                <Stat label="Total NET Pay" value={fmt(computedTotals.net)} cls="text-emerald-700 font-bold" />
                <Stat label="Cost to Company" value={fmt(computedTotals.gross + (preview.totals.employer_uif || 0))} cls="text-purple-700" />
              </div>
            )}

            {/* Action button */}
            <div className="flex justify-end mt-4 gap-2">
              <Button
                onClick={runPayroll}
                disabled={runBusy || locked || preview.lines.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="run-payroll-btn"
              >
                {runBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Calculator className="h-4 w-4 mr-1" />}
                {preview.existing_run ? "Re-generate Draft" : "Generate Payslips (DRAFT)"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ============ Past Runs Panel ============ */
function PastRunsPanel() {
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/api/payroll/runs`);
      setRuns(r.data.runs || []);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const view = async (id) => {
    const r = await axios.get(`${API}/api/payroll/runs/${id}`);
    setSelectedRun(r.data);
  };

  const lock = async (id) => {
    if (!window.confirm("Lock this run? Locked runs cannot be edited or deleted.")) return;
    await axios.post(`${API}/api/payroll/runs/${id}/lock`);
    toast.success("Locked");
    await load();
    if (selectedRun?.run?.id === id) view(id);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this run? All its payslips will be deleted.")) return;
    try {
      await axios.delete(`${API}/api/payroll/runs/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-base">Payroll History</CardTitle>
        <Button size="sm" variant="outline" onClick={load} disabled={busy}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </CardHeader>
      <CardContent>
        {runs.length === 0 ? (
          <p className="text-sm text-gray-400 italic py-6 text-center">No payroll runs yet.</p>
        ) : (
          <Table className="text-sm">
            <TableHeader>
              <TableRow>
                <TableHead>Period</TableHead>
                <TableHead>Staff</TableHead>
                <TableHead className="text-right">Gross</TableHead>
                <TableHead className="text-right">Deductions</TableHead>
                <TableHead className="text-right">NET</TableHead>
                <TableHead className="text-right">Cost to Co</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{MONTHS[r.month]} {r.year}</TableCell>
                  <TableCell>{r.employee_count}</TableCell>
                  <TableCell className="text-right">{fmt(r.total_gross)}</TableCell>
                  <TableCell className="text-right text-red-700">{fmt(r.total_deductions)}</TableCell>
                  <TableCell className="text-right font-bold text-emerald-700">{fmt(r.total_net_pay)}</TableCell>
                  <TableCell className="text-right">{fmt(r.cost_to_company)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={r.status === "locked" ? "border-amber-400 text-amber-700" : "border-gray-400 text-gray-700"}>
                      {r.status === "locked" ? "Locked" : "Draft"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-gray-500">{r.created_at?.slice(0, 10)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => view(r.id)} title="View payslips"><Eye className="h-4 w-4" /></Button>
                    {r.status !== "locked" && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => lock(r.id)} title="Lock"><Lock className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => remove(r.id)} title="Delete" className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* View payslips dialog */}
      <Dialog open={!!selectedRun} onOpenChange={(v) => !v && setSelectedRun(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          {selectedRun && (
            <>
              <DialogHeader>
                <DialogTitle>{MONTHS[selectedRun.run.month]} {selectedRun.run.year} — {selectedRun.run.employee_count} payslips</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                {selectedRun.payslips.map((p) => (
                  <PayslipCard key={p.id} slip={p} />
                ))}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => window.print()}><Printer className="h-4 w-4 mr-1" /> Print all</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ============ Single payslip card (print-friendly) ============ */
function PayslipCard({ slip }) {
  return (
    <div className="border border-gray-300 rounded p-4 print:break-inside-avoid print:break-after-page bg-white" data-testid={`payslip-${slip.employee_number}`}>
      <div className="flex justify-between items-start border-b pb-2 mb-3">
        <div>
          <div className="font-bold text-lg">{slip.employee_name}</div>
          <div className="text-xs text-gray-500">{slip.employee_number} · {slip.position}</div>
          <div className="text-xs text-gray-500">ID: {slip.id_number || "—"} · Tax #: {slip.tax_number || "—"}</div>
        </div>
        <div className="text-right">
          <div className="font-bold">Pay slip</div>
          <div className="text-xs text-gray-500">{slip.pay_period_start} → {slip.pay_period_end}</div>
          <div className="text-xs text-gray-500">Bank: {slip.bank_details?.bank_name} · {slip.bank_details?.account_number || "—"}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <div className="font-semibold mb-1 text-emerald-700">Earnings</div>
          <div className="space-y-0.5">
            <Row label={`Basic (${slip.salary_type === "hourly" ? fmtH(slip.hours_worked) + " hrs" : "fixed"})`} value={fmt(slip.basic_pay)} />
            {slip.overtime_hours > 0 && <Row label={`OT (${fmtH(slip.overtime_hours)} hrs @ 1.5×)`} value={fmt(slip.overtime_pay)} />}
            {slip.commission > 0 && <Row label="Commission" value={fmt(slip.commission)} />}
            {slip.bonus > 0 && <Row label="Bonus" value={fmt(slip.bonus)} />}
            {slip.other_earnings > 0 && <Row label={slip.other_earnings_description || "Other earnings"} value={fmt(slip.other_earnings)} />}
            <Row label="Gross" value={fmt(slip.gross_earnings)} bold />
          </div>
        </div>
        <div>
          <div className="font-semibold mb-1 text-red-700">Deductions</div>
          <div className="space-y-0.5">
            <Row label="PAYE" value={fmt(slip.paye_deduction)} />
            <Row label="UIF (1%)" value={fmt(slip.uif_deduction)} />
            {slip.loan_deduction > 0 && <Row label="Loan" value={fmt(slip.loan_deduction)} />}
            {slip.other_deductions > 0 && <Row label={slip.other_deductions_description || "Other"} value={fmt(slip.other_deductions)} />}
            <Row label="Total" value={fmt(slip.total_deductions)} bold />
          </div>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t flex justify-between items-center">
        <div className="text-xs text-gray-500">Employer UIF: {fmt(slip.employer_uif)} (paid separately)</div>
        <div className="text-right">
          <div className="text-xs text-gray-500">NET PAY</div>
          <div className="text-2xl font-bold text-emerald-700">{fmt(slip.net_pay)}</div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className={`flex justify-between ${bold ? "border-t pt-1 font-semibold" : ""}`}>
      <span>{label}</span><span>{value}</span>
    </div>
  );
}

function Stat({ label, value, cls = "" }) {
  return (
    <div className="border rounded p-2 bg-white">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-lg ${cls}`}>{value}</div>
    </div>
  );
}

/* ============ Config Panel ============ */
function ConfigPanel() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { (async () => { const r = await axios.get(`${API}/api/payroll/config`); setCfg(r.data); })(); }, []);

  if (!cfg) return <Card><CardContent className="p-6"><Loader2 className="h-4 w-4 animate-spin" /></CardContent></Card>;

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/api/payroll/config`, cfg);
      toast.success("Settings saved");
    } catch (e) {
      toast.error("Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Payroll Settings</CardTitle></CardHeader>
      <CardContent className="grid md:grid-cols-2 gap-4 max-w-3xl">
        <div>
          <Label>Standard hours per month</Label>
          <Input type="number" value={cfg.standard_hours_per_month} onChange={(e) => setCfg({ ...cfg, standard_hours_per_month: Number(e.target.value) })} />
          <p className="text-xs text-gray-500 mt-1">8h × 6d × 52/12 = 208 (48-hour week). Used to convert monthly salary → hourly rate for OT.</p>
        </div>
        <div>
          <Label>Daily normal hours (OT threshold)</Label>
          <Input type="number" value={cfg.daily_normal_hours} onChange={(e) => setCfg({ ...cfg, daily_normal_hours: Number(e.target.value) })} />
          <p className="text-xs text-gray-500 mt-1">Anything above this per day is auto-classified as overtime if the attendance row didn't split it.</p>
        </div>
        <div>
          <Label>Overtime multiplier</Label>
          <Input type="number" step="0.1" value={cfg.overtime_multiplier} onChange={(e) => setCfg({ ...cfg, overtime_multiplier: Number(e.target.value) })} />
          <p className="text-xs text-gray-500 mt-1">BCEA: 1.5× normal OT, 2× Sunday/public holiday (Sundays/holidays not auto yet).</p>
        </div>
        <div className="flex items-center gap-2 pt-6">
          <input type="checkbox" id="round-rand" checked={cfg.round_to_rand} onChange={(e) => setCfg({ ...cfg, round_to_rand: e.target.checked })} className="h-4 w-4" />
          <Label htmlFor="round-rand">Round net pay to nearest Rand</Label>
        </div>
        <div className="md:col-span-2">
          <Button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
            Save settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
